import React, { useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { BRAND_LUXE_PRIMARY, BRAND_SURFACE_CONTAINER_HIGHEST, BRAND_OUTLINE_VARIANT } from '../brandDashboardTokens';
import { RectangularImageCropperModal } from '../../../components/ImageCropper/RectangularImageCropperModal';
import { RICH_MEDIA_SHARE_ASPECT, RICH_MEDIA_SHARE_MAX_INPUT_BYTES } from '../../../constants/richMediaShareImage';
import { showToastMessage } from '../../../utils/AppUtils';

interface Props {
    preview: string | null;
    onSelect: (file: File, preview: string) => void;
    onClear: () => void;
}

export default function CampaignFeatureImagePicker({ preview, onSelect, onClear }: Props) {
    const ref = useRef<HTMLInputElement>(null);
    const [cropSrc, setCropSrc] = useState('');
    const [cropOpen, setCropOpen] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (file.size > RICH_MEDIA_SHARE_MAX_INPUT_BYTES) {
            showToastMessage('Image size must be under 5MB', 'info');
            return;
        }
        setCropSrc(URL.createObjectURL(file));
        setCropOpen(true);
    };

    const handleCropSave = (base64: string, file: File) => {
        onSelect(file, base64);
        setCropOpen(false);
        if (cropSrc.startsWith('blob:')) {
            URL.revokeObjectURL(cropSrc);
        }
        setCropSrc('');
    };

    const handleCropCancel = () => {
        setCropOpen(false);
        if (cropSrc.startsWith('blob:')) {
            URL.revokeObjectURL(cropSrc);
        }
        setCropSrc('');
    };

    return (
        <Box>
            <input ref={ref} type="file" accept="image/*" hidden onChange={handleChange} />

            {preview ? (
                <Box sx={{ position: 'relative', borderRadius: '12px', overflow: 'hidden' }}>
                    <Box
                        component="img"
                        src={preview}
                        alt=""
                        sx={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
                    />
                    <IconButton
                        onClick={onClear}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: '#fff',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                        }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    <Box
                        onClick={() => ref.current?.click()}
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(0,0,0,0)',
                            transition: 'background 0.2s',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.4)' },
                            '&:hover .replace-label': { opacity: 1 },
                        }}>
                        <Typography
                            className="replace-label"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                color: '#fff',
                                opacity: 0,
                                transition: 'opacity 0.2s',
                            }}>
                            Click to replace
                        </Typography>
                    </Box>
                </Box>
            ) : (
                <Box
                    onClick={() => ref.current?.click()}
                    sx={{
                        height: 120,
                        borderRadius: '12px',
                        border: `2px dashed ${BRAND_OUTLINE_VARIANT}50`,
                        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            borderColor: `${BRAND_LUXE_PRIMARY}60`,
                            bgcolor: 'rgba(255,201,101,0.04)',
                        },
                    }}>
                    <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 28, color: 'rgba(255,255,255,0.3)' }} />
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>
                        Add feature image
                    </Typography>
                </Box>
            )}

            <RectangularImageCropperModal
                open={cropOpen}
                src={cropSrc}
                onSave={handleCropSave}
                onCancel={handleCropCancel}
                aspect={RICH_MEDIA_SHARE_ASPECT}
                cropHeight={280}
                title="Crop feature image"
                variant="richMediaShare"
            />
        </Box>
    );
}
